import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrosshairs,
  faSpinner,
  faRotate,
  faCheck,
  faXmark,
  faQuestion,
  faDatabase,
} from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const CRITERIA = [
  { key: 'dayPct', label: '+10% day' },
  { key: 'rvol', label: '5× RVol' },
  { key: 'news', label: 'News' },
  { key: 'price', label: '$2–$20' },
  { key: 'float', label: 'Float <20M' },
];

function CheckDot({ pass, unknown }) {
  if (unknown) {
    return (
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-700"
        title="Unknown"
      >
        <FontAwesomeIcon icon={faQuestion} className="text-[8px]" />
      </span>
    );
  }
  return pass ? (
    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
      <FontAwesomeIcon icon={faCheck} className="text-[8px]" />
    </span>
  ) : (
    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-rose-100 text-rose-600">
      <FontAwesomeIcon icon={faXmark} className="text-[8px]" />
    </span>
  );
}

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function formatPrice(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `$${Number(v).toFixed(2)}`;
}

function formatRvol(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(1)}×`;
}

function formatFloat(m) {
  if (m == null || Number.isNaN(Number(m))) return '—';
  return `${Number(m).toFixed(1)}M`;
}

function formatCacheAge(iso) {
  if (!iso) return 'no cache yet';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const StockScanner = ({ onSymbolSelect }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [partial, setPartial] = useState([]);
  const [meta, setMeta] = useState(null);
  const [cache, setCache] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [error, setError] = useState(null);
  const [showNear, setShowNear] = useState(true);
  const [collectMsg, setCollectMsg] = useState('');

  const runScan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await marketDataApi.runStockScanner({ includePartial: true });
      setMatches(res.matches || []);
      setPartial(res.partial || []);
      setCache(res.cache || null);
      setMeta({
        scanned: res.scanned,
        enriched: res.enriched,
        yahooRefreshed: res.yahooRefreshed,
        elapsedMs: res.elapsedMs,
        criteria: res.criteria,
        asOf: res.asOf,
      });
    } catch (err) {
      console.error('Scanner error:', err);
      setError(err.response?.data?.error || err.message || 'Scan failed');
      setMatches([]);
      setPartial([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const collectData = useCallback(async () => {
    try {
      setCollecting(true);
      setCollectMsg('');
      setError(null);
      const res = await marketDataApi.collectScannerData({});
      const data = res.data || res;
      setCache(data);
      setCollectMsg(
        `Collected ${data.updated ?? 0} symbols · seed ${data.seedCount ?? 0} · ${(
          (data.elapsedMs || 0) / 1000
        ).toFixed(1)}s`
      );
      await runScan();
    } catch (err) {
      console.error('Scanner collect error:', err);
      setError(err.response?.data?.error || err.message || 'Collect failed');
    } finally {
      setCollecting(false);
    }
  }, [runScan]);

  useEffect(() => {
    runScan();
    marketDataApi
      .getScannerCache()
      .then((res) => setCache(res.data || res))
      .catch(() => {});
  }, [runScan]);

  const openSymbol = (symbol) => {
    if (onSymbolSelect) onSymbolSelect(symbol);
    else navigate(`/asset/${symbol}`);
  };

  const renderRow = (row, highlight) => (
    <tr
      key={row.symbol}
      className={`transition-colors cursor-pointer ${
        highlight ? 'bg-emerald-50/60 hover:bg-emerald-50' : 'hover:bg-gray-50'
      }`}
      onClick={() => openSymbol(row.symbol)}
    >
      <td className="px-2 py-1.5 font-semibold text-indigo-600 whitespace-nowrap">{row.symbol}</td>
      <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap">{formatPrice(row.price)}</td>
      <td
        className={`px-2 py-1.5 font-medium whitespace-nowrap ${
          (row.dayPct || 0) >= 0 ? 'text-emerald-700' : 'text-rose-600'
        }`}
      >
        {formatPct(row.dayPct)}
      </td>
      <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap">{formatRvol(row.rvol)}</td>
      <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap">{formatFloat(row.floatM)}</td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1" title={row.newsHeadlines?.[0] || 'No news'}>
          {CRITERIA.map((c) => (
            <CheckDot
              key={c.key}
              pass={!!row.checks?.[c.key]}
              unknown={c.key === 'float' && row.floatUnknown}
            />
          ))}
        </div>
      </td>
      <td className="px-2 py-1.5 text-gray-500 text-[10px] max-w-[140px] truncate" title={row.newsHeadlines?.[0]}>
        {row.newsHeadlines?.[0] || '—'}
      </td>
    </tr>
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faCrosshairs} className="text-xs text-rose-500" />
            Demand / Supply Scanner
          </h2>
          <p className="text-[10px] text-gray-500 mt-0.5">
            +10% day · 5× RVol · news · $2–$20 · float &lt;20M
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={collectData}
            disabled={collecting || loading}
            title="Collect float / avg volume into local cache"
            className="px-2 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          >
            {collecting ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
            ) : (
              <>
                <FontAwesomeIcon icon={faDatabase} className="text-xs mr-1" />
                Collect
              </>
            )}
          </button>
          <button
            type="button"
            onClick={runScan}
            disabled={loading || collecting}
            className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
            ) : (
              <FontAwesomeIcon icon={faRotate} className="text-xs" />
            )}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {CRITERIA.map((c) => (
          <span
            key={c.key}
            className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-600"
          >
            {c.label}
          </span>
        ))}
      </div>

      {(meta || cache) && (
        <p className="text-[10px] text-gray-400 mb-2">
          Scanned {meta?.scanned ?? '—'} · enriched {meta?.enriched ?? '—'}
          {meta?.yahooRefreshed != null ? ` · yahoo ${meta.yahooRefreshed}` : ''}
          {meta?.elapsedMs != null ? ` · ${(meta.elapsedMs / 1000).toFixed(1)}s` : ''}
          {cache ? (
            <>
              {' '}
              · cache {cache.symbolCount ?? 0} sym / seed {cache.seedCount ?? 0} (
              {formatCacheAge(cache.updatedAt)})
            </>
          ) : null}
        </p>
      )}
      {collectMsg && <p className="text-[10px] text-emerald-700 mb-2">{collectMsg}</p>}

      {error && (
        <div className="mb-2 p-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs">
          {error}
        </div>
      )}

      {loading && matches.length === 0 && partial.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-xs">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin mb-2" />
          <div>Scanning actives + gainers + cached low-float seed…</div>
        </div>
      ) : matches.length === 0 && partial.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-xs space-y-2">
          <div>No names hit all 5 criteria right now.</div>
          <div className="text-[10px] text-gray-400">
            Hit <span className="font-semibold">Collect</span> to build float/avg-volume cache, then scan again after the open.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">Symbol</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">Price</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">Day%</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">RVol</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">Float</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">Checks</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase">News</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {matches.map((row) => renderRow(row, true))}
              {showNear &&
                partial
                  .filter((p) => !matches.some((m) => m.symbol === p.symbol))
                  .map((row) => renderRow(row, false))}
            </tbody>
          </table>
        </div>
      )}

      {partial.length > 0 && (
        <button
          type="button"
          onClick={() => setShowNear((v) => !v)}
          className="mt-2 text-[10px] text-indigo-600 hover:text-indigo-800 font-medium"
        >
          {showNear ? 'Hide' : 'Show'} near misses ({partial.length})
        </button>
      )}
    </div>
  );
};

export default StockScanner;
